/** Small UI primitives: notices, spinner, confirm dialog, file drop, error boundary text. */
import * as Dialog from "@radix-ui/react-dialog";
import { type ReactNode, useRef, useState } from "react";

import { ApiError, errorMessage } from "../api/client";

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span className="row muted small" role="status">
      <span className="spinner" aria-hidden="true" /> {label}…
    </span>
  );
}

export function Notice({ kind = "info", title, children }: { kind?: "info" | "success" | "warning" | "error"; title?: string; children?: ReactNode }) {
  return (
    <div className={`notice ${kind}`} role={kind === "error" ? "alert" : "status"}>
      {title && <strong>{title}</strong>}
      {children}
    </div>
  );
}

/** Client-facing message first; technical detail behind a disclosure (DESIGN.md). */
export function ErrorNotice({ error, title }: { error: unknown; title?: string }) {
  if (!error) return null;
  const details = error instanceof ApiError ? error.details : null;
  return (
    <Notice kind="error" title={title}>
      {errorMessage(error)}
      {details != null && (
        <details style={{ marginTop: "0.4rem" }}>
          <summary className="small">Technical details</summary>
          <pre className="small mono" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
            {JSON.stringify(details, null, 2)}
          </pre>
        </details>
      )}
    </Notice>
  );
}

export function ConfirmDialog(props: {
  trigger: ReactNode;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<unknown> | void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { setOpen(next); setError(null); }}>
      <Dialog.Trigger asChild>{props.trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title asChild>
            <h3>{props.title}</h3>
          </Dialog.Title>
          <Dialog.Description asChild>
            <div className="muted" style={{ marginBottom: "1rem" }}>{props.description}</div>
          </Dialog.Description>
          <ErrorNotice error={error} />
          <div className="row end" style={{ marginTop: "1rem" }}>
            <Dialog.Close asChild>
              <button type="button" className="btn">Cancel</button>
            </Dialog.Close>
            <button
              type="button"
              className={`btn ${props.danger ? "danger" : "primary"}`}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await props.onConfirm();
                  setOpen(false);
                } catch (reason) {
                  setError(reason);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Working…" : props.confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function FileDrop(props: { accept?: string; multiple?: boolean; onFiles: (files: File[]) => void; label: ReactNode; disabled?: boolean }) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      className={`drop-zone${over ? " over" : ""}`}
      role="button"
      tabIndex={0}
      aria-disabled={props.disabled}
      onClick={() => !props.disabled && input.current?.click()}
      onKeyDown={(event) => {
        if ((event.key === "Enter" || event.key === " ") && !props.disabled) {
          event.preventDefault();
          input.current?.click();
        }
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        if (!props.disabled) props.onFiles(Array.from(event.dataTransfer.files));
      }}
    >
      {props.label}
      <input
        ref={input}
        type="file"
        hidden
        accept={props.accept}
        multiple={props.multiple}
        onChange={(event) => {
          props.onFiles(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
